import { useEffect, useRef } from "react";
import api from "@/lib/api";

/**
 * Sends an attendance heartbeat to the backend every `intervalMs` while the
 * student is actively on the page. Pauses when the tab is hidden or the user
 * has been idle (no pointer/keyboard event) for > `idleMs`.
 *
 * The backend clamps each pulse to 90 seconds max, so the client can't spoof.
 */
export default function useAttendanceHeartbeat({
    programId,
    lessonId,
    enabled = true,
    intervalMs = 30000,
    idleMs = 60000,
}) {
    const lastActivity = useRef(Date.now());
    const hidden = useRef(document.visibilityState === "hidden");
    const timer = useRef(null);

    useEffect(() => {
        if (!enabled || !programId) return undefined;

        const markActive = () => {
            lastActivity.current = Date.now();
        };
        const onVis = () => {
            hidden.current = document.visibilityState === "hidden";
            if (!hidden.current) markActive();
        };

        window.addEventListener("mousemove", markActive, { passive: true });
        window.addEventListener("keydown", markActive, { passive: true });
        window.addEventListener("scroll", markActive, { passive: true });
        window.addEventListener("click", markActive, { passive: true });
        document.addEventListener("visibilitychange", onVis);

        const tick = async () => {
            if (hidden.current) return;
            const idle = Date.now() - lastActivity.current;
            if (idle > idleMs) return;
            try {
                await api.post("/attendance/heartbeat", {
                    program_id: programId,
                    lesson_id: lessonId || null,
                    active_seconds: Math.round(intervalMs / 1000),
                });
            } catch {
                /* network / auth hiccups are non-fatal */
            }
        };

        // Fire one immediate pulse, then start the interval.
        tick();
        timer.current = window.setInterval(tick, intervalMs);

        return () => {
            window.removeEventListener("mousemove", markActive);
            window.removeEventListener("keydown", markActive);
            window.removeEventListener("scroll", markActive);
            window.removeEventListener("click", markActive);
            document.removeEventListener("visibilitychange", onVis);
            if (timer.current) window.clearInterval(timer.current);
            timer.current = null;
        };
    }, [programId, lessonId, enabled, intervalMs, idleMs]);
}
